<script setup lang="ts">
/**
 * The app frame of a Connect app: [sidebar] [main column] [aside].
 *
 * Desktop: the sidebar and the aside sit beside the main column, each
 * resizable by an overlay splitter handle on its edge (pointer, arrow keys,
 * Home/End) and collapsible through `v-model:sidebar-open` /
 * `v-model:aside-open`. Their widths are `v-model:sidebar-width` /
 * `v-model:aside-width` (px). The aside keeps its width when the window
 * resizes; the main column flexes.
 *
 * Narrow (below `narrowBelow` px): sidebar and aside become overlay drawers
 * over the main column with a backdrop — Escape or the backdrop closes them,
 * opening one closes the other, focus moves into the drawer and back to the
 * trigger on close. Entering the narrow mode closes both; leaving it restores
 * the desktop state. The same slot content stays mounted in both modes.
 *
 * The main column is the `header` slot on top and the default slot below,
 * which fills the remaining height (`min-height: 0`) and scrolls itself.
 * Every slot gets `{ narrow, sidebarOpen, asideOpen, toggleSidebar,
 * toggleAside, closeOverlays }`.
 *
 * With `storageKey` the open states and widths are remembered in the user's
 * settings (`shell.panes.<storageKey>`, through the kit's settings contract,
 * I-2510), so they follow the account.
 * It fills its host element — never the viewport — so it works inside the
 * Connect shell at any height.
 */
import { computed, nextTick, ref, useId, useSlots, watch, type Ref } from 'vue';
import { useMediaQuery } from '../Composables/observers';
import { getFocusableElements, trapTabKey } from '../Drawer/focus';
import { useNxPaneState } from '../Settings/settingsSource';
import NxSplitterGroup from '../Splitter/NxSplitterGroup.vue';
import NxSplitterPanel from '../Splitter/NxSplitterPanel.vue';
import NxSplitterResizeHandle from '../Splitter/NxSplitterResizeHandle.vue';
import type { NxSplitterPanelState } from '../Splitter/internal';
import NxWorkspaceRegion from './NxWorkspaceRegion.vue';

export interface NxWorkspaceSlotProps {
  /** The frame is in its narrow mode: sidebar and aside are overlay drawers. */
  narrow: boolean;
  sidebarOpen: boolean;
  asideOpen: boolean;
  toggleSidebar: () => void;
  toggleAside: () => void;
  /** Closes an open drawer in the narrow mode (after a navigation, say); no-op on desktop. */
  closeOverlays: () => void;
  /** Element id of the sidebar region, for a toggle's `aria-controls`. */
  sidebarId: string;
  /** Element id of the aside region, for a toggle's `aria-controls`. */
  asideId: string;
}

const props = withDefaults(
  defineProps<{
    /**
     * Smallest sidebar width (px). Every app keeps the kit's sidebar width,
     * minimum and maximum so all left columns look the same; don't override.
     */
    sidebarMinWidth?: number;
    /** Largest sidebar width (px). */
    sidebarMaxWidth?: number;
    /** Smallest aside width (px). */
    asideMinWidth?: number;
    /** Largest aside width (px). */
    asideMaxWidth?: number;
    /** Smallest main column width (px) a resize leaves on desktop. */
    mainMinWidth?: number;
    /** Remember open states and widths in the user's settings (`shell.panes.<storageKey>`). */
    storageKey?: string;
    /** Below this frame width (px, viewport) sidebar and aside become drawers. */
    narrowBelow?: number;
    /** Accessible name of the sidebar drawer (narrow mode). */
    sidebarLabel?: string;
    /** Accessible name of the aside drawer (narrow mode). */
    asideLabel?: string;
    /** Accessible name of the sidebar's resize handle. */
    sidebarResizeLabel?: string;
    /** Accessible name of the aside's resize handle. */
    asideResizeLabel?: string;
    /** Accessible name of the backdrop that closes a drawer. */
    closeOverlayLabel?: string;
    /** The regions float as one rounded card on the sunken app background. */
    floating?: boolean;
  }>(),
  {
    sidebarMinWidth: 200,
    sidebarMaxWidth: 360,
    asideMinWidth: 280,
    asideMaxWidth: 560,
    mainMinWidth: 320,
    storageKey: '',
    narrowBelow: 900,
    sidebarLabel: 'Seitenleiste',
    asideLabel: 'Seitenbereich',
    sidebarResizeLabel: 'Breite der Seitenleiste ändern',
    asideResizeLabel: 'Breite des Seitenbereichs ändern',
    closeOverlayLabel: 'Bereich schließen',
  }
);

const sidebarOpen = defineModel<boolean>('sidebarOpen', { default: true });
const asideOpen = defineModel<boolean>('asideOpen', { default: true });
const sidebarWidth = defineModel<number>('sidebarWidth', { default: 272 });
const asideWidth = defineModel<number>('asideWidth', { default: 340 });

defineSlots<{
  sidebar?: (props: NxWorkspaceSlotProps) => unknown;
  header?: (props: NxWorkspaceSlotProps) => unknown;
  default?: (props: NxWorkspaceSlotProps) => unknown;
  aside?: (props: NxWorkspaceSlotProps) => unknown;
}>();

const slots = useSlots();
// Read fresh on every use: a parent may add or drop a slot conditionally.
const hasSidebar = () => !!slots.sidebar;
const hasAside = () => !!slots.aside;

const sidebarId = `nx-workspace-sidebar-${useId()}`;
const asideId = `nx-workspace-aside-${useId()}`;

const narrow = useMediaQuery(() => `(max-width: ${Math.max(props.narrowBelow - 1, 0)}px)`);

/* ── Persistence ───────────────────────────────────────────────────────── */

/** What the workspace remembers under `shell.panes.<storageKey>`. */
interface WorkspacePaneState {
  sidebar_open: boolean;
  aside_open: boolean;
  sidebar_width: number;
  aside_width: number;
}

const pane = useNxPaneState<WorkspacePaneState>(() => props.storageKey);

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
const isWidth = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** The desktop open states, restored when the frame leaves the narrow mode. */
let desktopState: { sidebar: boolean; aside: boolean } | null = null;

/**
 * A stored value wins over the model's start value (so the parent's ref learns
 * it), also when it arrives later (the settings answered, a profile switch) —
 * in the narrow mode an open state goes to the desktop state the frame returns
 * to. Changes are written back, never the narrow mode's forced state.
 */
function persist<T extends boolean | number>(
  model: Ref<T>,
  field: keyof WorkspacePaneState,
  validate: (v: unknown) => v is T,
  region?: 'sidebar' | 'aside'
) {
  const stored = computed<T | null>(() => {
    const value = pane.state.value[field];
    return validate(value) ? value : null;
  });
  const adopt = (value: T | null) => {
    if (value === null) return;
    if (region && narrow.value && desktopState) desktopState[region] = value as boolean;
    else if (value !== model.value) model.value = value;
  };
  adopt(stored.value);
  watch(stored, adopt);
  watch(model, (value) => {
    if (!props.storageKey || narrow.value || value === stored.value) return;
    pane.patch({ [field]: value });
  });
}

persist(sidebarOpen, 'sidebar_open', isBoolean, 'sidebar');
persist(asideOpen, 'aside_open', isBoolean, 'aside');
persist(sidebarWidth, 'sidebar_width', isWidth);
persist(asideWidth, 'aside_width', isWidth);

/* ── Narrow mode: drawers ──────────────────────────────────────────────── */

watch(
  narrow,
  (isNarrow) => {
    if (isNarrow) {
      desktopState = { sidebar: sidebarOpen.value, aside: asideOpen.value };
      sidebarOpen.value = false;
      asideOpen.value = false;
    } else if (desktopState) {
      sidebarOpen.value = desktopState.sidebar;
      asideOpen.value = desktopState.aside;
      desktopState = null;
    }
  },
  { immediate: true, flush: 'sync' }
);

const sidebarElement = ref<InstanceType<typeof NxSplitterPanel> | null>(null);
const asideElement = ref<InstanceType<typeof NxSplitterPanel> | null>(null);
const elementOf = (panel: InstanceType<typeof NxSplitterPanel> | null) =>
  (panel?.$el as HTMLElement | undefined) ?? null;

const sidebarDrawerOpen = computed(() => narrow.value && sidebarOpen.value && hasSidebar());
const asideDrawerOpen = computed(() => narrow.value && asideOpen.value && hasAside());
const anyDrawerOpen = computed(() => sidebarDrawerOpen.value || asideDrawerOpen.value);

/** Where focus returns when a drawer closes: the element that opened it. */
const returnFocus: { sidebar: HTMLElement | null; aside: HTMLElement | null } = {
  sidebar: null,
  aside: null,
};

function watchDrawer(
  region: 'sidebar' | 'aside',
  isOpen: Readonly<Ref<boolean>>,
  panel: Ref<InstanceType<typeof NxSplitterPanel> | null>,
  closeOther: () => void
) {
  watch(isOpen, async (open) => {
    if (open) {
      closeOther();
      const active = document.activeElement;
      returnFocus[region] = active instanceof HTMLElement ? active : null;
      await nextTick();
      const el = elementOf(panel.value);
      if (!el || el.contains(document.activeElement)) return;
      (getFocusableElements(el)[0] ?? el).focus({ preventScroll: true });
    } else {
      const target = returnFocus[region];
      returnFocus[region] = null;
      const el = elementOf(panel.value);
      const active = document.activeElement;
      const focusLost = !active || active === document.body || !!el?.contains(active);
      if (target && focusLost && document.contains(target)) target.focus({ preventScroll: true });
    }
  });
}

watchDrawer('sidebar', sidebarDrawerOpen, sidebarElement, () => (asideOpen.value = false));
watchDrawer('aside', asideDrawerOpen, asideElement, () => (sidebarOpen.value = false));

function closeOverlays() {
  if (!narrow.value) return;
  sidebarOpen.value = false;
  asideOpen.value = false;
}

function onDrawerKeydown(event: KeyboardEvent, region: 'sidebar' | 'aside') {
  const open = region === 'sidebar' ? sidebarDrawerOpen.value : asideDrawerOpen.value;
  if (!open) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    if (region === 'sidebar') sidebarOpen.value = false;
    else asideOpen.value = false;
    return;
  }
  const el = elementOf(region === 'sidebar' ? sidebarElement.value : asideElement.value);
  if (el) trapTabKey(event, el);
}

/** The ARIA of a region: a modal dialog while it is an open drawer, nothing on desktop. */
function drawerAttrs(region: 'sidebar' | 'aside') {
  const open = region === 'sidebar' ? sidebarDrawerOpen.value : asideDrawerOpen.value;
  if (!open) return {};
  return {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': region === 'sidebar' ? props.sidebarLabel : props.asideLabel,
    tabindex: -1,
  };
}

/* ── Splitter wiring ───────────────────────────────────────────────────── */

// Plain functions, evaluated in the render: the slot presence is not reactive.
/** The sidebar shows (beside the main column or as an open drawer), for the region context. */
const sidebarVisible = () => hasSidebar() && sidebarOpen.value;
const outerState = (): NxSplitterPanelState =>
  hasSidebar() && sidebarOpen.value ? 'split' : 'only_second';
const innerState = (): NxSplitterPanelState =>
  hasAside() && asideOpen.value ? 'split' : 'only_first';
function onOuterState(state: NxSplitterPanelState | undefined) {
  if (state && hasSidebar()) sidebarOpen.value = state !== 'only_second';
}
function onInnerState(state: NxSplitterPanelState | undefined) {
  if (state && hasAside()) asideOpen.value = state !== 'only_first';
}

/* ── Slot props ────────────────────────────────────────────────────────── */

const slotProps = computed<NxWorkspaceSlotProps>(() => ({
  narrow: narrow.value,
  sidebarOpen: sidebarOpen.value,
  asideOpen: asideOpen.value,
  toggleSidebar: () => (sidebarOpen.value = !sidebarOpen.value),
  toggleAside: () => (asideOpen.value = !asideOpen.value),
  closeOverlays,
  sidebarId,
  asideId,
}));

const rootStyle = computed(() => ({
  '--workspace-sidebar-width': `${sidebarWidth.value}px`,
  '--workspace-aside-width': `${asideWidth.value}px`,
}));

/**
 * `narrow` (read-only): the frame is in its narrow (drawer) mode — for script
 * code of the app that must follow it (a reading pane that becomes a dialog),
 * so the app needs no media query of its own. Templates use the slot prop.
 */
defineExpose({ narrow });
</script>

<template>
  <div
    class="nx-workspace"
    :class="{ 'is-narrow': narrow, 'has-drawer': anyDrawerOpen, 'is-floating': floating }"
    :style="rootStyle"
    data-testid="nx-workspace"
  >
    <NxSplitterGroup
      v-model:size="sidebarWidth"
      :panel-state="outerState()"
      @update:panel-state="onOuterState"
      orientation="vertical"
      class="nx-workspace__frame"
    >
      <NxSplitterPanel
        :id="sidebarId"
        ref="sidebarElement"
        :min-size="props.sidebarMinWidth"
        :max-size="props.sidebarMaxWidth"
        class="nx-workspace__sidebar"
        :class="{ 'is-drawer': sidebarDrawerOpen }"
        data-testid="nx-workspace-sidebar"
        v-bind="drawerAttrs('sidebar')"
        @keydown="onDrawerKeydown($event, 'sidebar')"
      >
        <NxWorkspaceRegion
          v-if="hasSidebar()"
          region="sidebar"
          :narrow="narrow"
          :sidebar-visible="sidebarVisible()"
        >
          <slot name="sidebar" v-bind="slotProps" />
        </NxWorkspaceRegion>
      </NxSplitterPanel>
      <NxSplitterResizeHandle
        v-show="hasSidebar() && sidebarOpen && !narrow"
        variant="overlay"
        :label="props.sidebarResizeLabel"
        data-testid="nx-workspace-sidebar-handle"
      />
      <NxSplitterPanel class="nx-workspace__rest">
        <NxSplitterGroup
          v-model:size="asideWidth"
          :panel-state="innerState()"
          @update:panel-state="onInnerState"
          orientation="vertical"
          sized-panel="second"
          class="nx-workspace__frame"
        >
          <NxSplitterPanel
            :min-size="narrow ? undefined : props.mainMinWidth"
            class="nx-workspace__main"
          >
            <NxWorkspaceRegion region="main" :narrow="narrow" :sidebar-visible="sidebarVisible()">
              <div v-if="$slots.header" class="nx-workspace__header">
                <slot name="header" v-bind="slotProps" />
              </div>
              <div class="nx-workspace__content" data-testid="nx-workspace-content">
                <slot v-bind="slotProps" />
              </div>
            </NxWorkspaceRegion>
          </NxSplitterPanel>
          <NxSplitterResizeHandle
            v-show="hasAside() && asideOpen && !narrow"
            variant="overlay"
            :label="props.asideResizeLabel"
            data-testid="nx-workspace-aside-handle"
          />
          <NxSplitterPanel
            :id="asideId"
            ref="asideElement"
            :min-size="props.asideMinWidth"
            :max-size="props.asideMaxWidth"
            class="nx-workspace__aside"
            :class="{ 'is-drawer': asideDrawerOpen }"
            data-testid="nx-workspace-aside"
            v-bind="drawerAttrs('aside')"
            @keydown="onDrawerKeydown($event, 'aside')"
          >
            <NxWorkspaceRegion
              v-if="hasAside()"
              region="aside"
              :narrow="narrow"
              :sidebar-visible="sidebarVisible()"
            >
              <slot name="aside" v-bind="slotProps" />
            </NxWorkspaceRegion>
          </NxSplitterPanel>
        </NxSplitterGroup>
      </NxSplitterPanel>
    </NxSplitterGroup>
    <button
      v-if="anyDrawerOpen"
      type="button"
      tabindex="-1"
      class="nx-workspace__backdrop"
      :aria-label="props.closeOverlayLabel"
      data-testid="nx-workspace-backdrop"
      @click="closeOverlays"
    />
  </div>
</template>

<style scoped>
/* Fills its host (the Connect shell's content area) — never the viewport. */
.nx-workspace {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  isolation: isolate;
  background: var(--nxt-bg-app);
  color: var(--nxt-fg);
}

.nx-workspace__frame {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

.nx-workspace.is-floating:not(.is-narrow) {
  background: var(--nxt-surface-sunken-bg);
  padding: var(--nxt-space-xs);
  box-sizing: border-box;
}
.nx-workspace.is-floating:not(.is-narrow) .nx-workspace__frame {
  border-radius: var(--nxt-radius-xl);
  overflow: hidden;
  box-shadow: var(--nxt-shadow-sm);
}

/* Every region is a single-cell grid: its (single) child fills it. Prefixed
   with the root to win over the splitter panel's own overflow rule. */
.nx-workspace .nx-workspace__sidebar,
.nx-workspace .nx-workspace__aside,
.nx-workspace .nx-workspace__rest {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/* The overlay handles draw the edge lines between the regions. */
.nx-workspace .nx-workspace__sidebar,
.nx-workspace .nx-workspace__aside {
  background: var(--nxt-surface-bg);
}

.nx-workspace .nx-workspace__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--nxt-bg-app);
}

.nx-workspace__header {
  flex: 0 0 auto;
  min-width: 0;
}

.nx-workspace__content {
  display: grid;
  flex: 1 1 auto;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: auto;
}

/* ── Narrow: sidebar and aside as drawers over the main column ─────────── */

.nx-workspace.is-narrow .nx-workspace__sidebar,
.nx-workspace.is-narrow .nx-workspace__aside {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: calc(var(--nxt-z-above) + 1);
  /* The splitter's inline px size is the desktop width; a drawer takes the
     stored width but never more than most of the frame. */
  width: var(--workspace-sidebar-width);
  min-width: 0 !important;
  max-width: 88% !important;
  box-shadow: var(--nxt-shadow-lg);
  outline: none;
}

.nx-workspace.is-narrow .nx-workspace__sidebar {
  left: 0;
  border-right: var(--nxt-hairline) solid var(--nxt-border);
  animation: nx-workspace-in-start var(--nxt-motion-base) var(--nxt-ease-out);
}

.nx-workspace.is-narrow .nx-workspace__aside {
  right: 0;
  width: var(--workspace-aside-width);
  border-left: var(--nxt-hairline) solid var(--nxt-border);
  animation: nx-workspace-in-end var(--nxt-motion-base) var(--nxt-ease-out);
}

.nx-workspace__backdrop {
  position: absolute;
  inset: 0;
  z-index: var(--nxt-z-above);
  margin: 0;
  padding: 0;
  border: 0;
  background: var(--nxt-drawer-overlay-bg);
  cursor: default;
  animation: nx-workspace-fade var(--nxt-motion-base) var(--nxt-ease-out);
}

@keyframes nx-workspace-in-start {
  from {
    transform: translateX(-100%);
  }
}

@keyframes nx-workspace-in-end {
  from {
    transform: translateX(100%);
  }
}

@keyframes nx-workspace-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nx-workspace.is-narrow .nx-workspace__sidebar,
  .nx-workspace.is-narrow .nx-workspace__aside,
  .nx-workspace__backdrop {
    animation: none;
  }
}
</style>
